import { useState } from 'react';
import { useBasketAdd, useBasketAddArgs } from '~/features/basket';
import { useAlgoliaContextId, useProduct } from '~/shared/hooks';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';
import styles from './size-carousel.module.css';
import ContentSlider from '../content-slider/content-slider.component';
import { LineSpinner } from '../Spinners';
import Close from '$icons/close.svg';
import { Button } from '../button/button.component';
import { SizeSelector } from '../size-selector/size-selector.component';
import { ProductCardSize } from './product-card-size/product-card-size.component';

type Props = {
    contextId: string;
    headline?: string;
    buttonText?: string;
    trackingEvent?: useBasketAddArgs['trackingEvent'];
};
type ProductArgs = { productId: string; productColorId: string };

export const SizeCarousel = ({ contextId, headline, buttonText, trackingEvent }: Props) => {
    const { translate } = useTranslation();
    const { add, isLoading } = useBasketAdd();
    const hits = useAlgoliaContextId(contextId);
    const [currentProductArgs, setCurrentProductArgs] = useState<ProductArgs | null>(null);
    const { product, isLoading: basketIsLoading } = useProduct(currentProductArgs);

    const onSizeSelect = async (variantId: string) => {
        if (currentProductArgs?.productColorId) {
            await add({
                productId: currentProductArgs?.productColorId,
                variantId,
                quantity: 1,
                trackingEvent,
            });
        }
        setCurrentProductArgs(null);
    };

    return (
        <div className={styles.root}>
            <ContentSlider headline={headline} headlineVariant="display6" basket>
                {hits?.map((hit) => (
                    <ProductCardSize
                        key={hit.objectID}
                        hit={hit}
                        onClick={setCurrentProductArgs}
                        isLoading={isLoading}
                        buttonText={buttonText}
                        imagePriority="Product"
                    />
                )) || []}
            </ContentSlider>
            {product && (
                <div className={styles.sizesOverlay}>
                    <Button
                        className={styles.closeButton}
                        variant="tertiary"
                        size="small"
                        onClick={() => setCurrentProductArgs(null)}
                        withIcon
                    >
                        <Close title={translate('general.close')} />
                    </Button>

                    <SizeSelector inputName="sizes" onSelect={onSizeSelect} sizes={product.sizes} />
                </div>
            )}
            {(isLoading || basketIsLoading) && (
                <div className={styles.spinnerOverlay}>
                    <LineSpinner />
                </div>
            )}
        </div>
    );
};
